import React, { useMemo, useState } from 'react';
import { FINANCIALS_RANGE_OPTIONS } from './types';
import type { FinancialsWidgetInstanceConfig, FinancialsData } from './types';
import { formatCompact, formatPercent, DEFAULT_COLORS } from '../../../design';
import { useDataProviderWidget } from '../../../blocks/hooks';
import { ChartWidget, ChartRangeSelector, KeyMetricBadge, sliceByRows, redundantRangeValues } from '../../../blocks/chart-widget';
import type { ChartSpec } from '../../../blocks/chart-widget';
import type { BarChartSeries, ChartLegendSeries } from '../../../blocks/charts';

// Sample data shown during dashboard construction (no asset assigned).
const SAMPLE_DATA: FinancialsData = {
  ticker: 'SAMPLE',
  last_fiscal_year: 2025,
  last_revenue: 416161000000,
  last_net_income: 112010000000,
  last_net_margin: 0.269,
  // Most-recent-first, matching the backend contract the renderer reverses.
  financials_history: [
    { fiscal_year: 2025, revenue: 416161000000, gross_profit: 195201000000, operating_income: 133050000000, net_income: 112010000000, net_margin: 0.269 },
    { fiscal_year: 2024, revenue: 391035000000, gross_profit: 180683000000, operating_income: 123216000000, net_income: 93736000000, net_margin: 0.240 },
    { fiscal_year: 2023, revenue: 383285000000, gross_profit: 169148000000, operating_income: 114301000000, net_income: 96995000000, net_margin: 0.253 },
    { fiscal_year: 2022, revenue: 394328000000, gross_profit: 170782000000, operating_income: 119437000000, net_income: 99803000000, net_margin: 0.253 },
    { fiscal_year: 2021, revenue: 365817000000, gross_profit: 152836000000, operating_income: 108949000000, net_income: 94680000000, net_margin: 0.259 },
  ],
};

interface Props {
  config: FinancialsWidgetInstanceConfig;
  dashboardData?: Record<string, unknown>;
  onDataChange?: (dataKey: string, newData: unknown) => void;
  readOnly?: boolean;
  widgetInstanceId?: string;
}

const FinancialsWidgetRenderer: React.FC<Props> = ({
  config,
  dashboardData = {},
  widgetInstanceId,
}) => {
  const uid = dashboardData?.assetDataUid as string | undefined;
  const isPreview = !uid;

  const { data, loading, error, refetch } = useDataProviderWidget<FinancialsData>(
    'financials',
    uid,
    config as unknown as Record<string, unknown>,
    { enabled: !!uid, widgetInstanceId },
  );

  const d = uid ? data : SAMPLE_DATA;
  const [range, setRange] = useState<string>(config.defaultRange ?? 'ALL');

  // Backend returns most-recent-first; reverse for a left-to-right timeline.
  const fullChartData = useMemo(() => {
    const hist = d?.financials_history ?? [];
    return [...hist]
      .reverse()
      .filter((r) => typeof r.revenue === 'number')
      .map((r) => ({
        year: r.fiscal_year != null ? String(r.fiscal_year) : '',
        revenue: r.revenue,
        net_income: r.net_income,
      }));
  }, [d]);

  const disabledRanges = useMemo(
    () => redundantRangeValues(FINANCIALS_RANGE_OPTIONS, fullChartData),
    [fullChartData],
  );

  const chartData = useMemo(() => {
    const rows = FINANCIALS_RANGE_OPTIONS.find((o) => o.value === range)?.rows;
    return sliceByRows(fullChartData, rows);
  }, [fullChartData, range]);

  const series = useMemo<BarChartSeries[]>(() => [
    { yKey: 'revenue', yName: 'Revenue' },
    { yKey: 'net_income', yName: 'Net Income' },
  ], []);

  // Names-only legend — the header badges already carry the latest values.
  const legendSeries = useMemo<ChartLegendSeries[]>(() => series.map((s, i) => ({
    key: s.yKey,
    name: s.yName,
    color: DEFAULT_COLORS[i % DEFAULT_COLORS.length],
  })), [series]);

  const spec: ChartSpec = {
    chartKind: 'bar',
    rows: chartData,
    xKey: 'year',
    barSeries: series,
    showEndValueTags: false,
    yLabelFormatter: (params) => formatCompact(Number(params.value)),
    xTitle: 'Fiscal year',
    yTitles: { a: 'USD' },
    legendSeries,
    footnote: 'Source: FMP',
    emptyMessage: 'No financial history available',
  };

  const header = (
    <div className="flex flex-wrap items-center gap-2 px-3 py-2 border-b border-neutral-100 shrink-0">
      <div className="flex items-center gap-2 mr-1">
        <span className="text-sm font-semibold text-neutral-800">Financials</span>
        {d?.last_fiscal_year != null && (
          <span className="text-[11px] uppercase tracking-wide text-neutral-400">FY {d.last_fiscal_year}</span>
        )}
      </div>
      <KeyMetricBadge label="Revenue" value={formatCompact(d?.last_revenue ?? null)} />
      <KeyMetricBadge label="Net Income" value={formatCompact(d?.last_net_income ?? null)} />
      <KeyMetricBadge label="Net Margin" value={d?.last_net_margin != null ? formatPercent(d.last_net_margin * 100, 1) : '--'} />
      <ChartRangeSelector
        className="ml-auto"
        options={FINANCIALS_RANGE_OPTIONS}
        value={range}
        onChange={setRange}
        disabledValues={disabledRanges}
      />
    </div>
  );

  return (
    <ChartWidget
      loading={loading}
      error={error}
      refetch={refetch}
      isPreview={isPreview}
      spec={spec}
      header={header}
    />
  );
};

export default FinancialsWidgetRenderer;
